import * as THREE from "three";

export const hexToRgb = (hex) => {
    let r = 0,
        g = 0,
        b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }
    return { r, g, b };
};

export const rgbToHex = (r, g, b) =>
    `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;

export const lightenColor = (hex, amount) => {
    let { r, g, b } = hexToRgb(hex);
    r = Math.min(255, r + amount);
    g = Math.min(255, g + amount);
    b = Math.min(255, b + amount);
    return rgbToHex(r, g, b);
};

export const addAlpha = (hex, alpha) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getRoomPositionAndSize = (
    id,
    positions,
    scaleX,
    scaleY,
    canvasWidth,
    canvasHeight
) => {
    const positionData = positions.find((room) => room.id === id);
    return positionData
        ? {
            x: Math.floor(positionData.position.x * scaleX + canvasWidth / 2),
            z: Math.floor(positionData.position.z * scaleY + canvasHeight / 2),
        }
        : {
            x: undefined,
            z: undefined,
        };
};

export const loadPin = (obj, pinValues, scene) => {
    const pinColorMap = new Map();
    pinValues.data.forEach((material) =>
        pinColorMap.set(material.name, material.color)
    );
    obj.traverse((child) => {
        if (child instanceof THREE.Mesh) {
            child.material = new THREE.MeshPhysicalMaterial({
                color: pinColorMap.get(child.name),
                reflectivity: 0.5,
                roughness: 0.5,
                specularIntensity: 0.5,
            });
            child.renderOrder = 0;
        }
    });
    obj.position.y += 1;
    obj.position.x += 0;
    obj.position.z += -1.75;
    obj.rotateY(Math.PI / 4);
    obj.rotateX(-Math.PI / 16);
    scene.add(obj);
};

export const floorStructure = (obj, scene, floorValues, heatmap) => {
    const textureLoader = new THREE.TextureLoader();

    const textures = {
        emeExt: textureLoader.load(floorValues.emergencyExt),
        emeInt: textureLoader.load(floorValues.emergencyInt),
        esc01: textureLoader.load(floorValues.stairs01),
        esc02: textureLoader.load(floorValues.stairs02),
        topoEnt: textureLoader.load(floorValues.bathrooms),
    };

    Object.values(textures).forEach((texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
    });

    obj.traverse((child) => {
        if (child instanceof THREE.Mesh) {
            let materialOptions = {
                transparent: true,
                color: 0xffffff,
                specularIntensity: 1,
                reflectivity: 1,
                roughness: 0.2,
                opacity: 0.2,
                depthWrite: true,
                depthTest: true,
                blending: THREE.NormalBlending,
            };

            switch (child.name) {
            case "emergenciaExt":
                materialOptions.alphaMap = textures.emeExt;
                materialOptions.color = 0x000000;
                materialOptions.opacity = 1;
                break;
            case "emergenciaInt":
                materialOptions.map = textures.emeInt;
                materialOptions.transparent = false;
                materialOptions.opacity = 1;
                break;
            case "escadas_01":
                materialOptions.map = textures.esc01;
                materialOptions.transparent = false;
                materialOptions.opacity = 1;
                break;
            case "escada_02":
                materialOptions.map = textures.esc02;
                materialOptions.transparent = false;
                materialOptions.opacity = 1;
                break;
            case "elevador.banheiros":
                materialOptions.map = textures.topoEnt;
                materialOptions.transparent = false;
                materialOptions.opacity = 1;
                break;
            default:
                break;
            }

            if (child.name != "parede" && child.name != "emergenciaExt" && heatmap) {
                child.scale.y = 1.22;
            }

            child.material = new THREE.MeshPhysicalMaterial(materialOptions);
            child.renderOrder = 1;
        }
    });

    scene.add(obj);
};

export const floorObj = (obj, scene, texAlpha, texOcclusion) => {
    obj.traverse((child) => {
        if (child instanceof THREE.Mesh) {
            child.material = new THREE.MeshPhysicalMaterial({
                transparent: 1,
                alphaMap: texAlpha,
                map: texOcclusion,
                color: 0xdfdfdf,
                specularIntensity: 1,
                reflectivity: 0.8,
                roughness: 1,
            });
            child.renderOrder = 0;
        }
    });
    scene.add(obj);
};

export const ceillingObj = (obj, scene, heatmapTexture, opacity) => {
    obj.traverse((child) => {
        if (child instanceof THREE.Mesh) {
            child.material = new THREE.MeshPhysicalMaterial({
                color: 0x000000,
                emissive: 0xffffff,
                emissiveMap: heatmapTexture,
                transparent: true,
                opacity: opacity,
            });
            child.position.y += 0.01;
            child.renderOrder = 1;
        }
    });
    scene.add(obj);
};
